/**
 * The well: a translucent glass panel with `wellRows` rows, the brick stack
 * at the bottom, the falling sum block, and everything that happens when a
 * block resolves (timeline.ts): a correct block drops and breaks the top
 * layer into themed bits with a "+340" floater (sparkles on a clean floor); a
 * wrong or timed-out block drops and stretches into a new layer while a
 * friendly card shows the right answer. When the stack is high the rim glows
 * softly warm (no red flashing); at game over the well gives a gentle shake.
 */
import { useMemo, useRef } from 'react';
import type { GameState, Locale, ThemePack } from '@dropstack/core';
import { useT } from '../i18n/index.ts';
import { formatInteger } from '../screens/model/format.ts';
import { levelChangeTone } from './announce.ts';
import { AnswerCard, GameOverCard, LevelBanner } from './Cards.tsx';
import { Burst, Floater } from './Effects.tsx';
import { useElementSize } from './hooks.ts';
import { valueView } from './mathView.ts';
import { fallingView, isDanger } from './playfield.ts';
import { BrickStack, cssVars } from './Stack.tsx';
import { SumBlock } from './SumBlock.tsx';
import { landingDelayMs, resolutionFrame, stackView, TIMING } from './timeline.ts';

export interface PlayfieldProps {
  state: GameState;
  /** Engine time of this frame. */
  now: number;
  theme: ThemePack;
  locale: Locale;
  reducedMotion: boolean;
  /** "Larger text" multiplier for the maths. */
  textScale: number;
}

export function Playfield({ state, now, theme, locale, reducedMotion, textScale }: PlayfieldProps) {
  const t = useT();
  const innerRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(innerRef);
  const { wellRows } = state.config;
  const rowPx = size.height / wellRows;

  const frame = resolutionFrame(state, now);
  const falling = fallingView(state, now);
  const stack = stackView(state, frame);
  const res = frame?.resolution ?? null;
  const resolvedCorrect = res?.kind === 'correct';

  // The answer shown in the blank while a correct block drops (stable per resolution for MathText's memo).
  const fill = useMemo(
    () => (res && res.kind === 'correct' ? valueView(res.problem.answer, res.problem.answerKind, locale) : null),
    [res, locale],
  );

  const over = state.phase === 'gameOver';
  const endedByStack = over && !!frame?.endedRun;
  const showGameOverCard = endedByStack && !!res && frame.elapsed >= landingDelayMs(res.kind) + TIMING.STRETCH_MS;
  const ready = size.width > 0 && size.height > 0;

  let block = null;
  if (ready && falling && state.active) {
    block = (
      <SumBlock
        key={state.active.problem.id}
        problem={state.active.problem}
        locale={locale}
        mode={falling.resting ? 'resting' : 'falling'}
        row={falling.row}
        landingRow={falling.landingRow}
        rowPx={rowPx}
        wellWidth={size.width}
        timeLeft={falling.timeLeft}
        fill={null}
        textScale={textScale}
        reducedMotion={reducedMotion}
      />
    );
  } else if (ready && frame?.block && res) {
    block = (
      <SumBlock
        key={res.problem.id}
        problem={res.problem}
        locale={locale}
        mode={resolvedCorrect ? 'correct' : 'wrong'}
        row={frame.block.row}
        landingRow={wellRows - 1 - frame.bricksBefore}
        rowPx={rowPx}
        wellWidth={size.width}
        timeLeft={null}
        fill={fill}
        textScale={textScale}
        reducedMotion={reducedMotion}
      />
    );
  }

  // Where effects start: the row the block landed on (centre), px from the top.
  const landingY = frame ? (wellRows - 1 - frame.bricksBefore + 0.5) * rowPx : 0;
  const burstY = frame?.burst?.kind === 'layer' ? landingY + rowPx : landingY;

  const classes = [
    'well',
    isDanger(state) && !over && 'well--danger',
    endedByStack && !reducedMotion && 'well--shake',
    stack.layers === 0 && 'well--empty',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} aria-label={t('game.wellLabel', { bricks: state.bricks, max: wellRows - 1 })}>
      <div ref={innerRef} className="well__inner" style={cssVars({ '--rows': wellRows })}>
        <BrickStack
          brickStyle={theme.brickStyle}
          layers={stack.layers}
          breakingTop={stack.breakingTop}
          formingTop={stack.formingTop}
          reducedMotion={reducedMotion}
        />
        {block}
        {ready && frame?.burst && res && !reducedMotion && (
          <Burst
            kind={frame.burst.kind}
            brickStyle={theme.brickStyle}
            seed={res.problem.seq}
            progress={frame.burst.progress}
            y={burstY}
            spread={Math.min(size.width * 0.4, 170)}
          />
        )}
        {ready && frame?.floater != null && res && (
          <Floater
            text={`+${formatInteger(res.scoreDelta.raw)}`}
            note={res.brokeLayer ? undefined : t('game.cleanFloor')}
            progress={frame.floater}
            y={landingY}
            reducedMotion={reducedMotion}
          />
        )}
      </div>

      <div className="well__cards">
        {showGameOverCard && res ? (
          <GameOverCard missed={res.kind === 'correct' ? null : { problem: res.problem, locale }} />
        ) : frame?.reveal && res && res.kind !== 'correct' && !endedByStack ? (
          <AnswerCard kind={res.kind} problem={res.problem} locale={locale} />
        ) : state.phase === 'levelBanner' && state.pendingLevelChange ? (
          <LevelBanner change={state.pendingLevelChange} tone={levelChangeTone(state.pendingLevelChange, state.checkpoints)} locale={locale} />
        ) : null}
      </div>
    </section>
  );
}
